import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { XAxis, CartesianGrid, Bar, BarChart, Cell } from "recharts";
import { useNavigate, useSearchParams } from "react-router-dom";


import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  Skeleton,
  PageHeader,
  Button,
} from "@/components/ui";
import { BirthDayMonthPatientReportDataTable } from "@/components/common";

import {
  useDownloadReportBirthdaysToExcelMutation,
  useDownloadReportBirthdaysToPDFMutation,
  useFetchBirthDayMonthPatientReportQuery,
  useRequestReportBirthdaysPDFMutation,
} from "@/hooks";
import colors from "tailwindcss/colors";

export function BirthdaysReports() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [perPage, setPerPage] = useState(10);

  const today = new Date();
  const defualtMonth = today.getMonth() + 1;
  const page = searchParams.get("page");
  const month = searchParams.get("month") || defualtMonth.toString();
  const perPageParam = searchParams.get("per_page");

  const { data, isLoading, refetch } =
    useFetchBirthDayMonthPatientReportQuery({
      page: page || "1",
      month: month || "",
      perPage: perPageParam,
    });

  const { mutateAsync: requestPDF, isLoading: isLoadingRequestPDF } = useRequestReportBirthdaysPDFMutation();

  const { mutateAsync: downloadExcel, isLoading: isLoadingDownloadExcel } =
    useDownloadReportBirthdaysToExcelMutation({
      page: page || "1",
      month: month || "",
      perPage: perPageParam,
    });
  const { mutateAsync: downloadPDF, isLoading: isLoadingDownloadPDF } =
    useDownloadReportBirthdaysToPDFMutation({
      page: page || "1",
      month: month || "",
      perPage: perPageParam,
    });

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set("per_page", newPerPage.toString());
    setSearchParams(newSearchParams);
  };

  const handlePrint = async () => {
    try {
      const blob = await requestPDF({
        page: page || "1",
        month: month || "",
        perPage: perPageParam,
      });
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = blobUrl;

      document.body.appendChild(iframe);

      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();

          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
            document.body.removeChild(iframe);
          }, 1000);
        }, 500);
      };
    } catch (error) {
      console.error("Erro ao gerar PDF para impressão:", error);
    }
  };

  useEffect(() => {
    refetch();
  }, [page, perPageParam, month, refetch]);

  return (
    <section className="space-y-7">
      <PageHeader eyebrow={t("LABEL.reports")} title={t("LABEL.birthdays")} />
      <main className="space-y-7">
        {data && !isLoading ? (
          <ChartPatientBirthdayMonth
            chart_data={data.chart_data}
            chart_label={data.chart_label}
            onMonthSelect={async (monthIndex) => {
              if (monthIndex && String(monthIndex) !== searchParams.get("month")) {
                searchParams.set("month", String(monthIndex));
                await navigate({ search: searchParams.toString() });
              } else {
                searchParams.delete("month")
                await navigate({ search: searchParams.toString() });
              }
            }}
            defualtMonth={Number(month) - 1}
          />
        ) : (
          <Skeleton className="w-full h-[500px]" />
        )}

        {data && !isLoading ? (
          <div className="space-y-3">
            <div className="flex items-center justify-end gap-x-2">
              <Button
                variant="secondary"
                onClick={handlePrint}
                isLoading={isLoadingRequestPDF}>
                {t("LABEL.print")}
              </Button>
              <Button
                variant="secondary"
                onClick={() => downloadExcel()}
                isLoading={isLoadingDownloadExcel}
              >
                {t("LABEL.export excel")}
              </Button>
              <Button
                variant="secondary"
                onClick={() => downloadPDF()}
                isLoading={isLoadingDownloadPDF}
              >
                {t("LABEL.export pdf")}
              </Button>
            </div>
            <BirthDayMonthPatientReportDataTable
              handlePerPageChange={handlePerPageChange}
              perPage={perPage}
              data={data} />
          </div>
        ) : (
          <Skeleton className="w-full h-[500px]" />
        )}
      </main>
    </section>
  );
}

interface ChartPatientBirthdayMonthProps {
  chart_data: number[];
  chart_label: string[];
  onMonthSelect?: (monthIndex: number, monthName: string) => void;
  defualtMonth: number;
}

export default function ChartPatientBirthdayMonth({
  chart_data,
  chart_label,
  onMonthSelect,
  defualtMonth
}: ChartPatientBirthdayMonthProps) {
  const { t } = useTranslation();
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  const formattedData = chart_label.map((month, index) => ({
    month,
    total: chart_data[index],
  }));

  const chartConfig = {
    birthdays: {
      label: t("Aniversariantes"),
      color: "#8979ff",
    },
  };

  const formatMonthLabel = (month: string) => {
    const monthAbbreviation = month.slice(0, 3);
    return t(`MONTHS.${monthAbbreviation.toLowerCase()}`, monthAbbreviation);
  };

  const formatMonthToTooltip = (month: string) => {
    return t(`MONTHS.${month.toLowerCase()}`, month);
  };

  const handleBarClick = (data: any) => {
    const monthIndex = chart_label.indexOf(data.month) + 1;
    setSelectedMonth(data.month);
    if (onMonthSelect) {
      onMonthSelect(monthIndex, data.month);
    }
  };

  useEffect(() => {
    const currentMonthName = chart_label[defualtMonth];

    if (currentMonthName) {
      setSelectedMonth(currentMonthName);
    }
  }, [defualtMonth]);


  return (
    <div className="h-full">
      <ChartContainer
        className="min-h-[100px] max-h-[400px] w-full"
        config={chartConfig}
      >
        <BarChart
          accessibilityLayer
          height={200}
          data={formattedData}
          barSize={40}
        >
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="month"
            tickLine={false}
            tickMargin={10}
            axisLine={false}
            tickFormatter={formatMonthLabel}
          />
          <ChartTooltip
            labelFormatter={formatMonthToTooltip}
            content={<ChartTooltipContent />}
            wrapperClassName="mt-10"
          />
          <ChartLegend content={<ChartLegendContent />} />

          <Bar
            dataKey="total"
            radius={[8, 8, 0, 0]}
            onClick={handleBarClick}
          >
            {formattedData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.month === selectedMonth ? `${colors.teal[600]}` : chartConfig.birthdays.color}
                cursor="pointer"
              />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>
    </div>
  );
}
